import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CircularProgress, MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useComplaintsStats } from "hooks/useComplaintsStats";
import { useBranches } from "hooks/useBranches";
import { ComplaintsChartPoint } from "interfaces/entities/ticketStats";
import { buildTicketsHref, endOfMonthDate, startOfMonthDate } from "helpers/ticketsHref";

const COLOR_MONTH = "#4F46E5";
const COLOR_WEEK = "#22C55E";

// Дефолтный диапазон лет для селектора: текущий год и 4 предыдущих
const buildYearOptions = (): number[] => {
  const current = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => current - i);
};

interface ComplaintsChartProps {
  branchID?: number | string;
}

export const ComplaintsChart = ({ branchID }: ComplaintsChartProps = {}) => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [localBranchID, setLocalBranchID] = useState<number | "">("");
  const { data: branches = [] } = useBranches();
  const activeBranchID = localBranchID !== "" ? localBranchID : branchID;
  const { data, isLoading, error } = useComplaintsStats(year, activeBranchID);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<any>(null);

  // Карта период → диапазон дат для tooltip-ов недель
  const weekByPeriod = useMemo(() => {
    if (!data) return new Map<string, { label: string; startDate: string; endDate: string }>();
    return new Map(
      data.currentMonth.weekly.map((w) => [
        w.period,
        { label: w.label, startDate: w.startDate, endDate: w.endDate },
      ]),
    );
  }, [data]);

  const navigate = useNavigate();
  const goToComplaints = (point: ComplaintsChartPoint) => {
    // Тип всегда COMPLAINT (компонент строит график именно по жалобам).
    // Месяц → диапазон месяца; неделя → диапазон из weekByPeriod (startDate/endDate уже YYYY-MM-DD).
    let from = "";
    let to = "";
    if (point.type === "month") {
      from = startOfMonthDate(point.period);
      to = endOfMonthDate(point.period);
    } else {
      const w = weekByPeriod.get(point.period);
      if (w) {
        from = w.startDate;
        to = w.endDate;
      }
    }
    navigate(
      buildTicketsHref({
        type: "COMPLAINT",
        period: "custom",
        from,
        to,
        branchID: activeBranchID ? String(activeBranchID) : undefined,
      }),
    );
  };

  useEffect(() => {
    const ChartCtor = (window as any).Chart;
    if (!data || !canvasRef.current || !ChartCtor) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const points: ComplaintsChartPoint[] = data.chart;

    chartRef.current = new ChartCtor(canvasRef.current, {
      type: "bar",
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: "Жалоб",
            data: points.map((p) => p.count),
            backgroundColor: points.map((p) => (p.type === "month" ? COLOR_MONTH : COLOR_WEEK)),
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        onHover: (_evt: any, els: any[], chart: any) => {
          chart.canvas.style.cursor = els?.length ? "pointer" : "default";
        },
        onClick: (_evt: any, els: any[]) => {
          const idx = els?.[0]?.index;
          const point = idx != null ? points[idx] : undefined;
          if (point) goToComplaints(point);
        },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              generateLabels: () => [
                {
                  text: "Месяц",
                  fillStyle: COLOR_MONTH,
                  strokeStyle: COLOR_MONTH,
                  hidden: false,
                  index: 0,
                },
                {
                  text: "Неделя текущего месяца",
                  fillStyle: COLOR_WEEK,
                  strokeStyle: COLOR_WEEK,
                  hidden: false,
                  index: 1,
                },
              ],
            },
          },
          tooltip: {
            callbacks: {
              title: (items: any[]) => {
                if (!items?.length) return "";
                const point = points[items[0].dataIndex];
                if (!point) return "";
                if (point.type === "week") {
                  const w = weekByPeriod.get(point.period);
                  if (w) {
                    return `${w.label} (${w.startDate} — ${w.endDate})`;
                  }
                  return `Неделя: ${point.label}`;
                }
                return `Месяц: ${point.label}`;
              },
              label: (item: any) => `${item.parsed.y} шт.`,
            },
          },
        },
        scales: {
          x: {
            ticks: {
              autoSkip: false,
              maxRotation: 45,
              minRotation: 0,
            },
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            ticks: { precision: 0, stepSize: 1 },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, weekByPeriod]);

  return (
    <Card>
      <CardContent>
        <div className="flex flex-space-between items-center mb-2">
          <div>
            <h4 className="text-md font-bold mb-1">Количество жалоб за {year}</h4>
            {data && <div className="text-sm color-gray">Всего: {data.totalCount}</div>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              size="small"
              value={localBranchID}
              onChange={(e) => setLocalBranchID(e.target.value as number | "")}
              displayEmpty
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">Все филиалы</MenuItem>
              {branches?.data?.map((b: any) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
            <Select size="small" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {buildYearOptions().map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {isLoading && (
          <div
            className="text-center"
            style={{ height: 364, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <CircularProgress size={28} />
          </div>
        )}

        {!isLoading && error && (
          <div
            className="text-center"
            style={{ height: 364, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            Не удалось загрузить статистику
          </div>
        )}

        {!isLoading && !error && (
          <div style={{ height: 364, position: "relative" }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplaintsChart;
